//
/* GS://PROTOCOLO
 * Arquivo: src/pages/NFHistoryPage.jsx
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.4.0 – Leitura Híbrida (Retroativa) de Itens
 * Impacto da Versão: src/pages/NFHistoryPage.jsx
 * Data: 21/12/2025
 * Responsável: Gemini (IA)
 *
 * Mudanças desta versão:
 * - ADICIONADO: Função handleRowClick com busca sob demanda (lazy loading) na coleção 'nf_items'.
 * - MODIFICADO: Importação do 'where' do firebase/firestore.
 * - MODIFICADO: GS-LEITURA-RETROATIVA
 */
//

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore'; // GS-LEITURA-RETROATIVA: added where
import { db } from '../firebase/config';
import { getSuppliers } from '../services/firestoreService';
import { Loader2, Printer, Filter, ChevronDown, ChevronUp } from 'lucide-react';

const getNFDate = (nfEntry) => {
  if (!nfEntry) return null;
  const raw = nfEntry.nfDate ?? nfEntry.date ?? nfEntry.createdAt ?? null;
  if (!raw) return null;
  if (typeof raw?.toDate === 'function') { try { const d = raw.toDate(); return Number.isNaN(d.getTime()) ? null : d; } catch { } }
  if (typeof raw === 'object' && typeof raw.seconds === 'number') { const d = new Date(raw.seconds * 1000); return Number.isNaN(d.getTime()) ? null : d; }
  if (raw instanceof Date) { return Number.isNaN(raw.getTime()) ? null : raw; }
  if (typeof raw === 'string' || typeof raw === 'number') { const d = new Date(raw); return Number.isNaN(d.getTime()) ? null : d; }
  return null;
};

const pickNumber = (data, keys, fallback = 0) => {
  for (const key of keys) {
    if (!key) continue;
    const value = data[key];
    if (typeof value === 'number') return value;
    if (value !== undefined && value !== null && !Number.isNaN(Number(value))) { return Number(value); }
  }
  return fallback;
};

const normalizeNFEntry = (doc) => {
  const data = doc.data ? doc.data() : doc;
  const nfDate = getNFDate(data);
  const supplierName = data.supplierName || data.supplier_name || (data.supplier && (data.supplier.name || data.supplier.supplierName)) || 'N/A';
  const supplierId = data.supplierId || data.supplier_id || (data.supplier && (data.supplier.id || data.supplier.supplierId)) || '';
  const totalItems = pickNumber(data, ['totalItems', 'itemsCount', 'itemsQty'], 0);
  const grossTotal = pickNumber(data, ['grossTotal', 'itemsTotal', 'subtotal', 'gross_value'], 0);
  let expensesTotal = pickNumber(data, ['expensesTotal', 'expenses_total', 'totalExpenses'], 0);
  if (!expensesTotal && data.expenses && typeof data.expenses.total === 'number') { expensesTotal = data.expenses.total; }
  let grandTotal = pickNumber(data, ['grandTotal', 'totalNF', 'total', 'totalPurchaseValue'], 0);
  if (!grandTotal) { grandTotal = grossTotal + expensesTotal; }
  const lotNumber = data.lotNumber || data.mainLotNumber || data.lot_code || '';

  // Tenta localizar o array de itens em várias chaves possíveis e parseia JSON se necessário
  let items = data.items || data.products || data.itens || [];
  
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch (e) {
      items = [];
    }
  }

  if (!Array.isArray(items)) {
    items = [];
  }

  return { 
    id: doc.id || data.id, 
    ...data, 
    items, 
    _nfDate: nfDate, 
    _nfDateTime: nfDate ? nfDate.getTime() : 0, 
    _supplierName: supplierName, 
    _supplierId: supplierId, 
    _totalItems: totalItems, 
    _grossTotal: grossTotal, 
    _expensesTotal: expensesTotal, 
    _grandTotal: grandTotal, 
    _lotNumber: lotNumber, 
  };
};

const formatCurrency = (value) => (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDate = (date) => date ? date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A';

const NFHistoryPage = ({ openModal }) => {
  const [entries, setEntries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [error, setError] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterNFNumber, setFilterNFNumber] = useState('');
  const [filterSupplierId, setFilterSupplierId] = useState('ALL');
  
  const [expandedNF, setExpandedNF] = useState(null);
  const [loadingRowId, setLoadingRowId] = useState(null); // Estado para loading visual na linha

  const fetchSuppliers = useCallback(async () => {
    setIsLoadingSuppliers(true);
    try { const data = await getSuppliers(); setSuppliers(Array.isArray(data) ? data : []); } catch (e) { console.error('Erro ao carregar fornecedores:', e); } finally { setIsLoadingSuppliers(false); }
  }, []);

  const fetchNFEntries = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try { const snap = await getDocs(query(collection(db, 'nf_entries'), orderBy('nfDate', 'desc'))); const list = snap.docs.map((d) => normalizeNFEntry(d)); setEntries(list); } catch (e) { console.error('Erro ao carregar Histórico NF:', e); setError(e?.message || 'Erro ao carregar Histórico NF.'); if (openModal) { openModal(<p className="text-red-500">Erro técnico.</p>, 'Erro ao Carregar Histórico NF'); } } finally { setIsLoading(false); }
  }, [openModal]);

  useEffect(() => { fetchNFEntries(); fetchSuppliers(); }, [fetchNFEntries, fetchSuppliers]);

  // GS-LEITURA-RETROATIVA: Lógica inteligente de expansão
  const handleRowClick = async (nf) => {
    // Se estiver clicando na mesma linha aberta, fecha
    if (expandedNF === nf.id) {
      setExpandedNF(null);
      return;
    }

    // Se a NF já tem itens carregados (correção nova), abre direto
    if (nf.items && nf.items.length > 0) {
      setExpandedNF(nf.id);
      return;
    }

    // Se não tem itens, tenta buscar na coleção antiga 'nf_items'
    try {
      setLoadingRowId(nf.id); // Ativa loading visual na linha (opcional, ou apenas espera)
      
      const q = query(collection(db, 'nf_items'), where('nfId', '==', nf.id));
      const snapshot = await getDocs(q);
      
      const fetchedItems = snapshot.docs.map(doc => doc.data());

      if (fetchedItems.length > 0) {
        // Atualiza a lista local com os itens encontrados para não precisar buscar de novo
        setEntries(prev => prev.map(entry => {
          if (entry.id === nf.id) {
            return { ...entry, items: fetchedItems };
          }
          return entry;
        }));
      }

      setExpandedNF(nf.id);
    } catch (err) {
      console.error("Erro ao buscar itens retroativos:", err);
    } finally {
      setLoadingRowId(null);
    }
  };

  const filteredEntries = useMemo(() => {
    return entries.filter((nf) => {
      const d = nf._nfDate;
      if (filterStartDate) { const start = new Date(filterStartDate); start.setHours(0, 0, 0, 0); if (!d || d < start) return false; }
      if (filterEndDate) { const end = new Date(filterEndDate); end.setHours(23, 59, 59, 999); if (!d || d > end) return false; }
      if (filterNFNumber) { const nfNumber = String(nf.nfNumber || nf.nf_number || '').toLowerCase(); if (!nfNumber.includes(filterNFNumber.toLowerCase())) return false; }
      if (filterSupplierId && filterSupplierId !== 'ALL') { if (nf._supplierId !== filterSupplierId) return false; }
      return true;
    });
  }, [entries, filterStartDate, filterEndDate, filterNFNumber, filterSupplierId]);

  const handlePrint = () => {
    if (filteredEntries.length === 0) return;
    const reportContent = `<div style="padding: 20px;"><h1 style="text-align: center;">Histórico NF (Módulo Novo)</h1><table style="width: 100%; border-collapse: collapse;"><thead><tr><th>Data</th><th>NF</th><th>Fornecedor</th><th>Total</th></tr></thead><tbody>${filteredEntries.map(nf => `<tr><td>${formatDate(nf._nfDate)}</td><td>${nf.nfNumber}</td><td>${nf._supplierName}</td><td>${formatCurrency(nf._grandTotal)}</td></tr>`).join('')}</tbody></table></div>`;
    const printWindow = window.open('', '_blank', 'height=800,width=1200');
    if (printWindow) { printWindow.document.write(`<html><head><title>Histórico NF</title></head><body>${reportContent}<script>window.onload = function(){ window.print(); };</script></body></html>`); printWindow.document.close(); }
  };

  if (isLoading) return <div className="flex h-screen w-full items-center justify-center bg-brand-black text-white"><Loader2 className="h-12 w-12 animate-spin text-brand-gold" /></div>;

  return (
    <div className="p-4 md:p-8 bg-brand-black min-h-screen text-white font-sans">
      <div className="no-print-content flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div><h1 className="text-3xl font-bold font-serif mb-2">Histórico NF (Módulo Novo)</h1><p className="text-gray-300 text-sm">Visão consolidada das Notas Fiscais.</p></div>
        <button onClick={handlePrint} disabled={filteredEntries.length === 0} className="flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-600"><Printer className="mr-2 h-5 w-5" /> Imprimir Histórico</button>
      </div>

      <div className="no-print-content bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
        <div className="flex items-center gap-2 mb-4"><Filter className="h-5 w-5 text-brand-gold" /><h2 className="text-xl font-semibold text-gray-100">Filtros</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div><label className="block text-sm font-medium text-gray-400">Data inicial</label><input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} className="mt-1 block w-full bg-gray-800 border-gray-600 rounded p-2 text-white" /></div>
          <div><label className="block text-sm font-medium text-gray-400">Data final</label><input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} className="mt-1 block w-full bg-gray-800 border-gray-600 rounded p-2 text-white" /></div>
          <div><label className="block text-sm font-medium text-gray-400">Número da NF</label><input type="text" value={filterNFNumber} onChange={(e) => setFilterNFNumber(e.target.value)} placeholder="Digite o número" className="mt-1 block w-full bg-gray-800 border-gray-600 rounded p-2 text-white" /></div>
          <div><label className="block text-sm font-medium text-gray-400">Fornecedor</label><select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)} className="mt-1 block w-full bg-gray-800 border-gray-600 rounded p-2 text-white" disabled={isLoadingSuppliers}><option value="ALL">Todos</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        </div>
        <button onClick={fetchNFEntries} className="mt-4 px-6 py-2 bg-brand-gold text-brand-black font-semibold rounded-md hover:bg-yellow-500 transition-colors">Recarregar NF</button>
      </div>

      <div className="bg-brand-dark p-6 rounded-lg border border-gray-700">
        <div className="flex items-center justify-between mb-4"><h2 className="text-xl font-semibold text-gray-100">Lista de Notas Fiscais</h2><span className="text-sm text-gray-400">{filteredEntries.length} NF encontradas</span></div>
        {error && <div className="mb-4 bg-red-100 text-red-800 rounded px-4 py-3">{error}</div>}
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">NF</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fornecedor</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Itens</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="bg-brand-dark divide-y divide-gray-700">
              {filteredEntries.map((nf) => (
                <React.Fragment key={nf.id}>
                  {/* GS-LEITURA-RETROATIVA: onClick atualizado */}
                  <tr className="hover:bg-gray-800 cursor-pointer transition-colors" onClick={() => handleRowClick(nf)}>
                    <td className="px-4 py-3 text-sm text-gray-300 flex items-center">
                        {loadingRowId === nf.id ? (
                          <Loader2 size={14} className="mr-2 animate-spin text-brand-gold" />
                        ) : (
                          expandedNF === nf.id ? <ChevronUp size={14} className="mr-2"/> : <ChevronDown size={14} className="mr-2"/>
                        )}
                        {formatDate(nf._nfDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">{String(nf.nfNumber || '—')}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{nf._supplierName || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-200">{nf._totalItems || 0}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-brand-gold">{formatCurrency(nf._grandTotal)}</td>
                  </tr>
                  
                  {expandedNF === nf.id && (
                    <tr className="bg-black/30 animate-in fade-in duration-300">
                      <td colSpan="5" className="px-8 py-4">
                        <div className="border-l-2 border-brand-gold pl-4">
                          <h4 className="text-xs font-bold text-brand-gold uppercase mb-2">Itens da Nota</h4>
                          {nf.items && nf.items.length > 0 ? (
                              <table className="w-full text-xs text-gray-400">
                                <thead className="text-left text-gray-500 border-b border-gray-800">
                                  <tr><th className="py-1">Produto</th><th className="text-right">Qtd</th><th className="text-right">Vlr. Unit</th></tr>
                                </thead>
                                <tbody>
                                  {nf.items.map((item, idx) => (
                                    <tr key={idx} className="border-b border-gray-800/50">
                                      <td className="py-1.5">{item.name || item.productName || item.supplierItemName || 'Item sem nome'}</td>
                                      <td className="text-right">{item.quantity || 0}</td>
                                      <td className="text-right">{formatCurrency(item.price || item.unitPrice || item.unitValue || 0)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                          ) : (
                              <div className="space-y-2">
                                <p className="text-xs text-gray-500 italic">Nenhum item detalhado disponível para esta nota.</p>
                                <div className="p-2 bg-gray-900 rounded border border-gray-700 text-[10px] text-gray-500 font-mono break-all">
                                  DEBUG INFO: ID={nf.id} | Keys={Object.keys(nf).join(', ')}
                                </div>
                              </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NFHistoryPage;